import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import api from '../services/api';
import { User, ApiResponse } from '../types';

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  isAuthenticated: boolean;
  isAdmin: boolean;
  isEmployer: boolean;
  isEmployee: boolean;
  isTreasurer: boolean;
  isSecretary: boolean;
  isDirector: boolean;
  canProcessPayroll: boolean;
  canApprovePayroll: boolean;
  canApproveLeave: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('token');
      if (token) {
        try {
          const response = await api.getMe() as ApiResponse<User>;
          if (response.success && response.data) {
            setUser(response.data);
          } else {
            localStorage.removeItem('token');
          }
        } catch (error) {
          console.error('Failed to fetch user:', error);
          localStorage.removeItem('token');
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (email: string, password: string) => {
    const response = await api.login(email, password) as ApiResponse<{ token: string; user: User }>;

    if (response.success && response.data) {
      localStorage.setItem('token', response.data.token);
      setUser(response.data.user);
    } else {
      throw new Error(response.error || 'Login failed');
    }
  };

  const logout = () => {
    localStorage.removeItem('token');
    setUser(null);
    window.location.href = '/login';
  };

  const role = user?.role;
  const isStaff = !!(role && role !== 'EMPLOYEE');
  const value: AuthContextType = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    isAdmin: role === 'ADMIN',
    isEmployer: isStaff,
    isEmployee: role === 'EMPLOYEE',
    isDirector: role === 'DIRECTOR',
    isTreasurer: role === 'TREASURER',
    isSecretary: role === 'SECRETARY',
    canProcessPayroll: role === 'ADMIN' || role === 'TREASURER',
    canApprovePayroll: role === 'ADMIN' || role === 'SECRETARY',
    canApproveLeave: role === 'ADMIN' || role === 'SECRETARY',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
